<script setup lang="ts">
import {
  NumericQuantityDto,
  QuantityValueType,
  ReferencingObjectDto,
  SetupQuantityUsageDto,
  UnitDto,
} from '@/api/generator';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import Select from '@/components/forms/Select/Select.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { useUnitsStore } from '@/stores/unitsStore';
import { idEqual } from '@/utils/compareAndSort';
import { toRangeDisplay } from '@/utils/numberUtils';
import { useField, useFormValues } from 'vee-validate';
import { computed, onMounted, ref, toRef, watch } from 'vue';
import GreenBoxHolder from './GreenBoxHolder.vue';
import { decimalsOptions, valueTypeOptions } from './quantityFormHelpers';
import Range from './Range.vue';
import SetupScopeForm from './SetupScopeForm.vue';
import GeometriesForm from './useInEvaluations/GeometriesForm.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { isPredefinedEntity } from '@/utils/helper';

const unitsStore = useUnitsStore();

const props = defineProps<{
  name: string;
}>();

type Quantity = NumericQuantityDto;

const mode = useFormMode();

const { value: formValues } = useFormValues<Quantity>();

const { value, setValue } = useField<SetupQuantityUsageDto | undefined>(toRef(() => props.name));

const selectedUnit = ref<UnitDto | undefined>(undefined);

const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const initialSetupUsage = Boolean(value.value?.isUsageEnabled);

function resetParameters() {
  const defaultDecimals = {
    [QuantityValueType.FloatingPoint]: 3,
    [QuantityValueType.IntegerNumber]: 0,
  };

  if (value.value) {
    value.value.decimalPlaces = value.value.valueType
      ? defaultDecimals[value.value.valueType]
      : undefined;
    value.value.inputRangeMax = undefined;
    value.value.inputRangeMin = undefined;
  }
}
function updateSelectedUnit(unit: UnitDto | undefined): void {
  selectedUnit.value = unit;
  if (unit?.id && value.value) {
    const usage: SetupQuantityUsageDto = {
      ...value.value,
      unitId: unit.id,
    };
    setValue(usage);
  }
}

watch(() => value.value?.valueType, resetParameters);

watch(
  () => value.value?.unitId,
  (unitIdNew) => {
    if (unitIdNew) {
      selectedUnit.value = unitsStore.getById(unitIdNew);
    }

    resetParameters();
  },
);

onMounted(async () => {
  await unitsStore.initialize();
  // set the initially selected Unit
  if (value.value?.unitId) {
    selectedUnit.value = unitsStore.getById(value.value.unitId);
  }
});

const allowExtend = computed(
  () => (isReferenced.value || isPredefined.value) && isUsageEnabled.value,
);

const isInEditMode = mode.value === 'edit';
const isInAddMode = mode.value === 'add';
const isPredefined = computed(() => isPredefinedEntity(formValues));
const isReferenced = computed(() => formValues.isReferenced ?? false);
const isDisabled = computed(() => !isUsageEnabled.value && isInEditMode);
const isUsageEnabled = computed(() => value?.value?.isUsageEnabled ?? false);
const usageTurnedOn = computed(() => !initialSetupUsage && isUsageEnabled.value);

const isEditable = computed(() => {
  if (isUsageEnabled.value) {
    if (isInAddMode) return true;
    if (isInEditMode && !isPredefined.value) return true;
  }
  return false;
});

const isDeletable = computed(() => {
  if (isUsageEnabled.value) {
    if (isInAddMode) return true;
    if (isInEditMode) {
      if (!isPredefined.value && !isReferenced.value) return true;
      if (isPredefined.value || isReferenced.value) return false;
    }
  }
  return false;
});

const disabledOnReferenced = (disable: boolean = false) =>
  !(usageTurnedOn.value && isInEditMode) &&
  (!isUsageEnabled.value || (isInEditMode && isReferenced.value && disable));

const disabledOnPredefined = (disable: boolean = false) =>
  !(usageTurnedOn.value && isInEditMode) &&
  (!isUsageEnabled.value || (isInEditMode && isPredefined.value && disable));

function handleUseInSetupChange(useInSetup: boolean) {
  if (value.value && !useInSetup) {
    value.value.setupGeometries = value.value?.setupGeometries?.filter((geom) => !!geom.name);
  }
}
</script>

<template>
  <div class="d-flex flex-column ga-6">
    <InlineNotification
      v-if="(formValues.isReferenced || isPredefined) && mode === 'edit'"
      variant="flat"
      :value="{
        type: 'info',
        message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
          entity: $t('Quantity_one'),
        }),
      }"
    >
      <template #append>
        <span
          v-if="formValues.isReferenced"
          class="ml-2 linkButton"
          @click.stop="() => (entityReferences = formValues.isReferencedBy ?? null)"
          >{{ $t('Info') }}</span
        >
        <EntityReferencesModal
          v-if="formValues.isReferenced"
          :title="$t('ReferenceingEntities')"
          :references="entityReferences"
          @dialogOpen="
            (isOpen: boolean) => {
              if (!isOpen) entityReferences = null;
            }
          "
        ></EntityReferencesModal>
      </template>
    </InlineNotification>

    <GreenBoxHolder v-if="mode !== 'details'" :active="!!value?.isUsageEnabled">
      <VeeCheckbox
        :hideDetails="true"
        :noValidationOnCheck="true"
        name="setupUsage.isUsageEnabled"
        :label="$t('UseInSetup_other')"
        :disabled="
          mode === 'edit' && initialSetupUsage && (formValues.isReferenced || isPredefined)
        "
        @change="handleUseInSetupChange($event as boolean)"
      />
    </GreenBoxHolder>

    <template v-if="!value?.isUsageEnabled && mode === 'details'">
      <p class="text-body-1 font-weight-bold text-primary-lighten-2">
        {{ $t('QuantityNotUsedInSetupsMsg') }}
      </p>
    </template>

    <FormSection v-if="value" :disabled="disabledOnPredefined() || disabledOnReferenced()">
      <v-card
        ><v-card-text>
          <div class="d-flex flex-row">
            <VeeSelect
              name="setupUsage.valueType"
              :label="$t('ValueType')"
              class="mb-6 mr-6"
              :disabled="disabledOnPredefined(false) || disabledOnReferenced(true)"
              :items="valueTypeOptions"
            />

            <Select
              :label="$t('Unit_one')"
              :modelValue="selectedUnit"
              :items="unitsStore.options"
              :style="{ width: '110px' }"
              :disabled="disabledOnPredefined() || disabledOnReferenced(true)"
              :valueComparator="idEqual"
              class="mb-6 mr-6"
              :mode="mode"
              @update:modelValue="updateSelectedUnit"
            />

            <VeeSelect
              :disabled="
                value?.valueType !== 'FloatingPoint' ||
                disabledOnPredefined() ||
                disabledOnReferenced()
              "
              :items="decimalsOptions"
              :label="$t('DisplayedDecimals')"
              :placeholder="$t('Required')"
              name="setupUsage.decimalPlaces"
              class="mb-6 mr-6"
            />

            <span>
              <Range
                v-if="mode !== 'details'"
                :label="$t('Range')"
                :disabled="disabledOnPredefined(false) || disabledOnReferenced(true)"
              >
                <template #min>
                  <VeeNumberField
                    name="setupUsage.inputRangeMin"
                    class="w-8"
                    :format="{ valueType: value.valueType }"
                    :disabled="disabledOnPredefined(false) || disabledOnReferenced(true)"
                    :prefix="$t('Min')"
                    :suffix="selectedUnit?.abbreviation"
                  />
                </template>

                <template #max>
                  <VeeNumberField
                    name="setupUsage.inputRangeMax"
                    class="w-8 mr-6"
                    :format="{
                      valueType: value.valueType,
                    }"
                    :disabled="disabledOnPredefined(false) || disabledOnReferenced(true)"
                    :prefix="$t('Max')"
                    :suffix="selectedUnit?.abbreviation"
                  />
                </template>
              </Range>

              <TextField
                v-else
                :label="$t('Range')"
                :mode="'details'"
                :modelValue="
                  toRangeDisplay(
                    value.inputRangeMin,
                    value.inputRangeMax,
                    selectedUnit?.abbreviation,
                  )
                "
              />
            </span></div></v-card-text
      ></v-card>
    </FormSection>

    <GeometriesForm
      name="setupUsage.setupGeometries"
      :disabled="isDisabled"
      :isEditable="isEditable"
      :isDeletable="isDeletable"
      :style="{ opacity: !isUsageEnabled ? 0.5 : 1 }"
      :title="$t('SetupGeometry_other')"
      :tooltipText="$t('AddSetupGeometry')"
      :deleteTooltipText="$t('DeleteSetupGeometry')"
    />

    <SetupScopeForm
      name="setupUsage"
      :disabled="isDisabled"
      :allowExtend="allowExtend"
      :style="{ opacity: !isUsageEnabled ? 0.5 : 1 }"
    />
  </div>
</template>
